'use client';

import { useQuery } from '@tanstack/react-query';
import { getInteractionAggregates } from '@/api/generated/interactions/interactions';
import type { GetInteractionAggregatesReqFiltersV2Item } from '@/api/generated/model';
import { UserDimension } from '@/api/generated/model';
import { QUERY_KEY_INTERACTION_AGGREGATES } from '@/constants/query-keys';
import type { AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import { resolveMethodologyDimensions, type MethodologyDimension } from '@/domains/pages/ai-proficiency/fixtures/methodology';
import { STATIC_LEVEL_DIMENSION_TARGETS } from '@/domains/pages/ai-proficiency/fixtures/upskilling-benchmarks';
import { buildNearPromotionAifiProficiencyFilter, GROUP_BY_AIFI_PROFICIENCY, METHODOLOGY_AGGREGATE_VARIABLES, UPSKILLING_LEVEL_LADDER_METHODOLOGY_VARIABLES } from '@/domains/pages/ai-proficiency/utils/aifi-aggregates-api';
import { resolveAiProficiencyAggregateTimeRange } from '@/domains/pages/ai-proficiency/utils/api-payloads';
import { mapLevelSnapshots, type LevelSnapshot } from '@/domains/pages/ai-proficiency/utils/map-aggregates';
import { mapSegmentSummaries, type SegmentSummary } from '@/domains/pages/ai-proficiency/utils/map-segment-aggregates';
import { buildSegmentFilterV2Values, buildSegmentGroupBy, segmentKindLabel, type SegmentKind } from '@/domains/pages/ai-proficiency/utils/segment-api-payloads';
import { mapLevelMethodologyFromAggregates, mapPromotionTransitions, resolveUpskillingChartsLoading, sumPromotionCounts, type PromotionTransition } from '@/domains/pages/ai-proficiency/utils/upskilling';
import { isQueryCacheMissing } from '@/domains/pages/ai-proficiency/utils/query-loading';
import { AI_PROFICIENCY_PAGE_QUERY_OPTIONS, aiProficiencyMethodologyDimensionsQueryKey, aiProficiencySegmentsQueryKey, getAiProficiencyTimeRangeQueryKey, upskillingLevelLadderMethodologyQueryKey } from '@/domains/pages/ai-proficiency/query-options';
import { useCallback, useMemo } from 'react';
import { useAiProficiencyEffectiveNow, useAiProficiencyStableNow } from '@/domains/pages/ai-proficiency/hooks/use-ai-proficiency-stable-now';
import { useUserDimensionMapping } from '@/domains/pages/shared/use-user-dimension-mapping';
import { applySegmentDisplayNames, getSegmentDisplayName } from '@/domains/pages/ai-proficiency/utils/segment-display';
export type UpskillingView = 'all' | SegmentKind;
export type UpskillingPageData = {
  view: UpskillingView;
  segmentKind: SegmentKind | null;
  segmentNoun: string;
  segmentNounPlural: string;
  selectedSegmentName: string;
  segments: SegmentSummary[];
  levels: LevelSnapshot[];
  totalUsers: number;
  orgMethodologyDimensions: MethodologyDimension[] | null;
  levelMethodology: Partial<Record<AifiLevel, MethodologyDimension[]>>;
  levelTargets: typeof STATIC_LEVEL_DIMENSION_TARGETS;
  promotionTransitions: PromotionTransition[];
  totalCloseToPromotion: number;
  isLoading: boolean;
  isInitialLoading: boolean;
  isChartsLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  formatSegmentName: (name: string) => string;
};
type UseUpskillingPageDataParams = {
  projectId: string | undefined;
  view: UpskillingView;
  selectedSegmentName: string | undefined;
};
function buildScopeFilters(view: UpskillingView, selectedSegmentName: string): GetInteractionAggregatesReqFiltersV2Item[] {
  if (view === 'all' || !selectedSegmentName) return [];
  return [buildSegmentFilterV2Values(view, [selectedSegmentName])];
}
export function useUpskillingPageData({
  projectId,
  view,
  selectedSegmentName
}: UseUpskillingPageDataParams): UpskillingPageData {
  const stableNow = useAiProficiencyStableNow();
  const {
    effectiveNow,
    isReady: isEffectiveNowReady
  } = useAiProficiencyEffectiveNow();
  const stableNowKey = stableNow.getTime();
  const effectiveNowKey = effectiveNow.getTime();
  const aggregateTimeRange = useMemo(() => resolveAiProficiencyAggregateTimeRange(stableNow, effectiveNow), [stableNowKey, effectiveNowKey, stableNow, effectiveNow]);
  const aggregateTimeRangeQueryKey = useMemo(() => getAiProficiencyTimeRangeQueryKey(aggregateTimeRange), [aggregateTimeRange]);
  const enabled = Boolean(projectId && aggregateTimeRange && isEffectiveNowReady);
  const segmentKind = view === 'all' ? null : view;
  const {
    hasMapping,
    isLoading: isLoadingMapping
  } = useUserDimensionMapping(segmentKind ?? UserDimension.team, enabled && segmentKind != null);
  const segmentNoun = segmentKind ? segmentKindLabel(segmentKind) : 'org';
  const segmentNounPlural = segmentKind ? segmentKindLabel(segmentKind, true) : 'org';
  const segmentsQuery = useQuery({
    queryKey: aiProficiencySegmentsQueryKey(segmentKind!, projectId!, aggregateTimeRangeQueryKey),
    queryFn: ({
      signal
    }) => {
      if (!segmentKind) {
        throw new Error('Segment list query requires team, market, or tenure');
      }
      return getInteractionAggregates({
        project_id: projectId!,
        time_range: aggregateTimeRange!,
        filters: [],
        variables: ['aifi_score', 'n_users'],
        group_by: buildSegmentGroupBy(segmentKind),
        calculate_previous: true,
        limit: 100
      }, {
        signal
      });
    },
    enabled: enabled && segmentKind != null,
    ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
  });
  const segments = useMemo(() => {
    const raw = segmentsQuery.data ? mapSegmentSummaries(segmentsQuery.data) : [];
    return applySegmentDisplayNames(raw, hasMapping, segmentKind ?? undefined);
  }, [hasMapping, segmentKind, segmentsQuery.data]);
  const formatSegmentName = useCallback((name: string) => getSegmentDisplayName(name, segments, hasMapping, segmentKind ?? undefined), [hasMapping, segmentKind, segments]);
  const resolvedSelectedName = useMemo(() => {
    if (view === 'all') return '';
    if (selectedSegmentName && segments.some(segment => segment.name === selectedSegmentName)) {
      return selectedSegmentName;
    }
    return segments[0]?.name ?? '';
  }, [segments, selectedSegmentName, view]);
  const segmentListFetched = view === 'all' || segmentsQuery.isFetched;
  const hasNoSegments = view !== 'all' && segmentListFetched && segments.length === 0;
  const selectedSegment = useMemo(() => segments.find(segment_0 => segment_0.name === resolvedSelectedName), [segments, resolvedSelectedName]);
  const selectedSegmentHasNoUsers = view !== 'all' && selectedSegment != null && selectedSegment.userCount === 0;
  const scopeReady = enabled && !hasNoSegments && !selectedSegmentHasNoUsers && (view === 'all' || segmentListFetched && Boolean(resolvedSelectedName));
  const scopeFilters = useMemo(() => buildScopeFilters(view, resolvedSelectedName), [resolvedSelectedName, view]);
  const levelLadderMethodologyQuery = useQuery({
    queryKey: upskillingLevelLadderMethodologyQueryKey(view, resolvedSelectedName, projectId!, aggregateTimeRangeQueryKey),
    queryFn: ({
      signal: signal_0
    }) => getInteractionAggregates({
      project_id: projectId!,
      time_range: aggregateTimeRange!,
      filters: [],
      filters_v2: scopeFilters,
      variables: UPSKILLING_LEVEL_LADDER_METHODOLOGY_VARIABLES,
      group_by: GROUP_BY_AIFI_PROFICIENCY,
      limit: 100
    }, {
      signal: signal_0
    }),
    enabled: scopeReady,
    ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
  });
  const orgMethodologyQuery = useQuery({
    queryKey: aiProficiencyMethodologyDimensionsQueryKey('org', projectId!, aggregateTimeRangeQueryKey, []),
    queryFn: ({
      signal: signal_1
    }) => getInteractionAggregates({
      project_id: projectId!,
      time_range: aggregateTimeRange!,
      filters: [],
      variables: METHODOLOGY_AGGREGATE_VARIABLES,
      limit: 1
    }, {
      signal: signal_1
    }),
    enabled: scopeReady,
    ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
  });
  const promotionQuery = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'upskilling-promotion', view, resolvedSelectedName, projectId, aggregateTimeRangeQueryKey],
    queryFn: ({
      signal: signal_2
    }) => getInteractionAggregates({
      project_id: projectId!,
      time_range: aggregateTimeRange!,
      filters: [],
      filters_v2: [buildNearPromotionAifiProficiencyFilter(), ...scopeFilters],
      variables: ['n_users'],
      group_by: GROUP_BY_AIFI_PROFICIENCY,
      limit: 10
    }, {
      signal: signal_2
    }),
    enabled: scopeReady,
    ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
  });
  const levels = useMemo(() => {
    if (!levelLadderMethodologyQuery.data) return [];
    return mapLevelSnapshots(levelLadderMethodologyQuery.data);
  }, [levelLadderMethodologyQuery.data]);
  const totalUsers = useMemo(() => {
    if (selectedSegmentHasNoUsers) {
      return selectedSegment?.userCount ?? 0;
    }
    return levels.reduce((sum, level) => sum + level.userCount, 0);
  }, [levels, selectedSegment, selectedSegmentHasNoUsers]);
  const orgMethodologyDimensions = useMemo(() => resolveMethodologyDimensions(orgMethodologyQuery.data?.data[0]), [orgMethodologyQuery.data]);
  const levelMethodology = useMemo(() => mapLevelMethodologyFromAggregates(levelLadderMethodologyQuery.data), [levelLadderMethodologyQuery.data]);
  const promotionTransitions = useMemo(() => mapPromotionTransitions(promotionQuery.data, levels), [levels, promotionQuery.data]);
  const totalCloseToPromotion = useMemo(() => sumPromotionCounts(promotionTransitions), [promotionTransitions]);
  const scopeQueriesLoading = isQueryCacheMissing(levelLadderMethodologyQuery) || isQueryCacheMissing(orgMethodologyQuery) || isQueryCacheMissing(promotionQuery);
  const isInitialLoading = !isEffectiveNowReady || enabled && (view === 'all' && isQueryCacheMissing(levelLadderMethodologyQuery) || view !== 'all' && (isQueryCacheMissing(segmentsQuery) || isLoadingMapping));
  const isChartsLoading = resolveUpskillingChartsLoading({
    view,
    segmentListFetched,
    segmentsCount: segments.length,
    selectedSegmentUserCount: selectedSegment?.userCount ?? null,
    scopeReady,
    scopeQueriesLoading
  });
  const isLoading = isInitialLoading;
  const isError = segmentsQuery.isError || levelLadderMethodologyQuery.isError || orgMethodologyQuery.isError || promotionQuery.isError;
  const error = segmentsQuery.error ?? levelLadderMethodologyQuery.error ?? orgMethodologyQuery.error ?? promotionQuery.error;
  const refetch = useCallback(() => {
    void segmentsQuery.refetch();
    void levelLadderMethodologyQuery.refetch();
    void orgMethodologyQuery.refetch();
    void promotionQuery.refetch();
  }, [levelLadderMethodologyQuery.refetch, orgMethodologyQuery.refetch, promotionQuery.refetch, segmentsQuery.refetch]);
  return {
    view,
    segmentKind,
    segmentNoun,
    segmentNounPlural,
    selectedSegmentName: resolvedSelectedName,
    segments,
    levels,
    totalUsers,
    orgMethodologyDimensions,
    levelMethodology,
    levelTargets: STATIC_LEVEL_DIMENSION_TARGETS,
    promotionTransitions,
    totalCloseToPromotion,
    isLoading,
    isInitialLoading,
    isChartsLoading,
    isError,
    error,
    refetch,
    formatSegmentName
  };
}